import { Brain, Database, Eye, Lock, Search } from "lucide-react";
import { useEffect, useState } from "react";

interface Props {
  propertyName: string;
  onAnalysisComplete: () => void;
}

interface ExpertTip {
  challenge: string;
  tip: string;
  insight: string;
  duration: number;
}

const expertTips: ExpertTip[] = [
  {
    challenge: "foot-traffic",
    tip: "Culture isn't a cost center - it's a revenue multiplier that justifies 15-25% rent premiums.",
    insight:
      "Our cultural sub-brands create authentic community value that tenants will pay premium for.",
    duration: 6000,
  },
  {
    challenge: "revenue-optimization",
    tip: "Diversification through random ventures fails. But sub-brands that align with your space's cultural identity create natural revenue streams.",
    insight:
      "We've seen property developers add 40% revenue through culturally-aligned magazines, events, and membership programs.",
    duration: 7000,
  },
  {
    challenge: "customer-engagement",
    tip: "Communities form around shared interests and values, not around your product or service. Think cultural bridge, not sales funnel.",
    insight:
      "Sub-brands become the cultural bridge between your business objectives and community needs.",
    duration: 6500,
  },
];

const strategicBusinessQuestions = [
  "Why should customers choose them over competition?",
  "What's their realistic CapEx budget for transformation?",
  "What's the compelling value proposition?",
  "How are current market conditions affecting decisions?",
  "Who are the key decision makers in the process?",
  "What's their timeline for seeing results?",
  "What's their biggest fear about transformation projects?",
  "How do they currently measure success?",
];

const codeAnalysisPhases = {
  initial: [
    "ANALYZING: buzzword_density = ['authentic', 'community', 'experience']",
    "PROCESSING: competitor_strategies = generic_marketing_approach()",
    "MAPPING: cultural_trends = {youth_culture: 0.73, sustainability: 0.84}",
    "EVALUATING: current_brand_resonance = 0.23 // LOW",
    "IDENTIFYING: opportunity_gaps = ['cultural_vacuum', 'authentic_storytelling']",
  ],
  strategic: [
    "ANALYZING: property_profile = {location: 'prime', footfall: 'high'}",
    "CALCULATING: budget_signals = capex_indicators.process()",
    "EVALUATING: competitive_pressure = market_conditions.assess()",
    "PROCESSING: value_proposition_gaps = current_positioning.analyze()",
    "IDENTIFYING: procurement_triggers = urgency_indicators.scan()",
  ],
  synthesis: [
    "CALCULATING: sub_brand_potential = high_cultural_alignment * audience_gap",
    "OPTIMIZING: content_strategy = lifestyle_integration + community_values",
    "FORECASTING: engagement_uplift = 3.2x baseline_metrics",
    "SYNTHESIZING: transformation_pathway = cultural_bridge_methodology",
    "GENERATING: implementation_roadmap = prioritized_action_items",
  ],
};

export default function AnalysisLoading({
  propertyName,
  onAnalysisComplete,
}: Props) {
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisPhase, setAnalysisPhase] = useState<
    "initial" | "strategic" | "synthesis"
  >("initial");
  const [codeLines, setCodeLines] = useState<string[]>([]);
  const [strategicQuestions, setStrategicQuestions] = useState<string[]>([]);
  const [currentTip, setCurrentTip] = useState<ExpertTip | null>(null);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    startAnalysis();
  }, []);

  const startAnalysis = () => {
    setAnalysisProgress(0);
    setAnalysisPhase("initial");
    setCodeLines([]);
    setStrategicQuestions([]);

    // 30-second analysis with phases
    const progressTimer = setInterval(() => {
      setAnalysisProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressTimer);
          setTimeout(() => onAnalysisComplete(), 1000);
          return 100;
        }

        // Phase transitions
        if (prev < 30) {
          setAnalysisPhase("initial");
        } else if (prev < 70) {
          setAnalysisPhase("strategic");
          // Show strategic questions during this phase
          if (prev > 35 && strategicQuestions.length === 0) {
            setStrategicQuestions(strategicBusinessQuestions);
          }
        } else {
          setAnalysisPhase("synthesis");
          setStrategicQuestions([]); // Hide questions in final phase
        }

        return prev + 0.167; // 30 seconds total
      });
    }, 50);

    // Show expert tips
    const showTips = () => {
      const randomTip =
        expertTips[Math.floor(Math.random() * expertTips.length)];
      setCurrentTip(randomTip);
      setShowTip(true);

      setTimeout(() => setShowTip(false), randomTip.duration);
      setTimeout(showTips, randomTip.duration + 4000);
    };

    setTimeout(showTips, 3000);

    // Animate code analysis based on phase
    const animateCode = () => {
      let codeIndex = 0;
      const codeTimer = setInterval(() => {
        const currentPhaseCode = codeAnalysisPhases[analysisPhase];
        if (codeIndex < currentPhaseCode.length) {
          setCodeLines((prev) => [...prev, currentPhaseCode[codeIndex]]);
          codeIndex++;
        } else {
          clearInterval(codeTimer);
          // Reset for next phase
          setTimeout(() => {
            setCodeLines([]);
            codeIndex = 0;
            if (analysisPhase !== "synthesis") {
              animateCode();
            }
          }, 2000);
        }
      }, 800);
    };

    animateCode();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 text-white relative overflow-hidden">
      {/* Dynamic animated background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-blue-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute top-2/3 right-1/4 w-32 h-32 bg-cyan-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-36 h-36 bg-purple-500 rounded-full filter blur-3xl animate-pulse delay-500"></div>
        <div className="absolute top-1/2 right-1/2 w-28 h-28 bg-green-500 rounded-full filter blur-3xl animate-pulse delay-700"></div>
      </div>

      {/* More spacious container with bigger margins */}
      <div className="relative z-10 min-h-screen px-6 py-12 sm:px-8 sm:py-16 md:px-12 md:py-20 lg:px-16 lg:py-24">
        <div className="max-w-6xl mx-auto space-y-12 sm:space-y-16 md:space-y-20">
          {/* Header Section */}
          <div className="text-center">
            <div className="inline-flex items-center bg-cyan-500/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-cyan-500/30 animate-pulse">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-ping mr-3"></div>
              <span className="text-cyan-400 text-xs sm:text-sm font-medium">
                CULTURAL ANALYSIS ENGINE • PROCESSING
              </span>
            </div>

            <h3 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
              Deep Cultural Analysis
            </h3>
            <p className="text-lg sm:text-xl text-slate-300 mb-6 leading-relaxed max-w-3xl mx-auto">
              {analysisPhase === "initial" && "Scanning cultural landscape..."}
              {analysisPhase === "strategic" &&
                "Analyzing strategic business factors..."}
              {analysisPhase === "synthesis" &&
                "Synthesizing transformation strategy..."}
            </p>

            <div className="max-w-md mx-auto mb-6">
              <div className="bg-slate-700/30 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full transition-all duration-100 rounded-full"
                  style={{ width: `${analysisProgress}%` }}
                />
              </div>
              <div className="text-sm text-slate-400 mt-2">
                {Math.round(analysisProgress)}% complete
              </div>
            </div>

            <div className="text-sm text-slate-400 mb-8">
              Analyzing {propertyName} • Cultural Intelligence Engine v2.1
            </div>
          </div>

          {/* Expert Tips Section - Always same size, never changes layout */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-500/10 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-blue-500/30 shadow-2xl min-h-[140px] sm:min-h-[160px] flex flex-col justify-center">
              <div className="flex items-center mb-3">
                <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse mr-3"></div>
                <span className="text-blue-400 text-sm font-medium">
                  EXPERT INSIGHT
                </span>
              </div>
              <div
                className={`transition-opacity duration-1000 ${
                  showTip && currentTip ? "opacity-100" : "opacity-20"
                }`}
              >
                <p className="text-white text-base md:text-lg mb-3 leading-relaxed">
                  {showTip && currentTip
                    ? currentTip.tip
                    : "Our cultural intelligence engine is analyzing your property data against 10,000+ case studies..."}
                </p>
                <p className="text-slate-300 text-sm italic">
                  {showTip && currentTip
                    ? currentTip.insight
                    : "Identifying unique cultural opportunities specific to your market and business challenges."}
                </p>
              </div>
            </div>
          </div>

          {/* Main Analysis Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Progress Overview */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-600/50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
                  <div className="flex items-center space-x-3 mb-4 sm:mb-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center animate-spin">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-white">
                        Cultural Intelligence Analysis
                      </h4>
                      <p className="text-slate-400">
                        Phase:{" "}
                        {analysisPhase.charAt(0).toUpperCase() +
                          analysisPhase.slice(1)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl sm:text-3xl font-bold text-cyan-400">
                      {Math.round(analysisProgress)}%
                    </div>
                    <div className="text-slate-400 text-sm">Complete</div>
                  </div>
                </div>

                {/* Phase indicator */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {["initial", "strategic", "synthesis"].map((phase, index) => (
                    <div
                      key={phase}
                      className={`p-3 rounded-lg text-center transition-all duration-500 ${
                        analysisPhase === phase
                          ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-300 border"
                          : analysisProgress > (index + 1) * 33
                          ? "bg-green-500/20 border-green-500/50 text-green-300 border"
                          : "bg-slate-700/50 text-slate-500"
                      }`}
                    >
                      <div className="text-sm font-semibold capitalize">
                        {phase}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Strategic Questions Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-purple-500/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30 h-fit min-h-[200px] flex flex-col justify-center">
                <div className="text-sm text-purple-400 mb-4 flex items-center">
                  <Search className="w-4 h-4 mr-2" />
                  Strategic Analysis
                </div>
                <div
                  className={`space-y-3 transition-opacity duration-500 ${
                    analysisPhase === "strategic" &&
                    strategicQuestions.length > 0
                      ? "opacity-100"
                      : "opacity-30"
                  }`}
                >
                  {(strategicQuestions.length > 0
                    ? strategicQuestions
                    : [
                        "Identifying key decision makers...",
                        "Assessing budget indicators...",
                        "Mapping competitive landscape...",
                        "Evaluating transformation timeline...",
                        "Analyzing success metrics...",
                        "Processing market conditions...",
                      ]
                  )
                    .slice(0, 6)
                    .map((question, index) => (
                      <div
                        key={index}
                        className="text-sm text-gray-300 animate-pulse"
                      >
                        • {question}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Code Terminal */}
          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-slate-600/30">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
              <div className="text-xs sm:text-sm text-green-400 font-mono flex items-center mb-2 sm:mb-0">
                <Database className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                Cultural Analysis Engine v2.1 • Phase: {analysisPhase}
              </div>
              <div className="flex items-center text-slate-500 text-xs">
                <Eye className="w-3 h-3 mr-1" />
                <span>Live Analysis</span>
                <Lock className="w-3 h-3 ml-2" />
              </div>
            </div>

            <div className="space-y-1 max-h-48 sm:max-h-64 overflow-y-auto min-h-[120px]">
              {codeLines.length === 0 ? (
                <div className="text-xs font-mono text-gray-500 animate-pulse">
                  <span className="text-green-400">{">"}</span> Initializing
                  analysis engine...
                </div>
              ) : (
                codeLines.map((line, index) => (
                  <div
                    key={index}
                    className="text-xs sm:text-sm font-mono text-gray-400 opacity-70 animate-pulse"
                  >
                    <span className="text-green-400">{">"}</span> {line}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Bottom status */}
          <div className="text-center">
            <p className="text-slate-400 text-sm">
              This analysis typically takes 30 seconds. Please don't refresh the
              page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
